/**
 * Recent jobs table component
 * Displays latest jobs on the dashboard
 */

import { Table, Progress, Button, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { JobStatusBadge } from '@/components/common/StatusBadge';
import type { Job } from '@/types/job';
import { formatNumber, formatRelativeTime, formatDuration } from '@/utils/formatters';

const { Link } = Typography;

interface RecentJobsTableProps {
  jobs: Job[];
  loading?: boolean;
  maxItems?: number;
}

export function RecentJobsTable({ jobs, loading, maxItems = 5 }: RecentJobsTableProps) {
  const navigate = useNavigate();

  const columns: ColumnsType<Job> = [
    {
      title: 'Query',
      dataIndex: 'searchQuery',
      key: 'query',
      ellipsis: true,
      render: (query: string, record) => (
        <div>
          <Link
            onClick={() => navigate(`/jobs/${record.id}`)}
            style={{ fontWeight: 500 }}
          >
            {query}
          </Link>
          {(record.locationDisplay || record.locationName) && (
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
              {record.locationDisplay || record.locationName}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: Job['status']) => (
        <JobStatusBadge status={status} size="small" />
      ),
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 150,
      render: (_, record) => {
        const percent = record.totalCells > 0
          ? Math.round((record.completedCells / record.totalCells) * 100)
          : 0;

        return (
          <Progress
            percent={percent}
            size="small"
            status={
              record.status === 'failed'
                ? 'exception'
                : record.status === 'completed'
                ? 'success'
                : 'active'
            }
            format={(val) => `${val}%`}
          />
        );
      },
    },
    {
      title: 'Places',
      dataIndex: 'placesFound',
      key: 'places',
      width: 100,
      align: 'right',
      render: (places: number) => formatNumber(places),
    },
    {
      title: 'Time',
      key: 'time',
      width: 100,
      render: (_, record) => {
        if (record.status === 'pending') {
          return '-';
        }
        if (record.completedAt && record.startedAt) {
          const duration = new Date(record.completedAt).getTime() - new Date(record.startedAt).getTime();
          return formatDuration(duration);
        }
        if (record.startedAt) {
          return formatRelativeTime(record.startedAt);
        }
        return formatRelativeTime(record.createdAt);
      },
    },
    {
      title: '',
      key: 'actions',
      width: 48,
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/jobs/${record.id}`)}
          aria-label={`View job: ${record.searchQuery}`}
        />
      ),
    },
  ];

  const data = jobs.slice(0, maxItems);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Typography.Title level={5} style={{ margin: 0 }}>
          Recent Jobs
        </Typography.Title>
        <Button
          type="link"
          onClick={() => navigate('/jobs')}
          style={{ padding: 0 }}
        >
          View All <RightOutlined />
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
        locale={{ emptyText: 'No jobs yet' }}
        onRow={(record) => ({
          onClick: () => navigate(`/jobs/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}

export default RecentJobsTable;
